import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Text } from 'shared/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory } from 'react-router-dom'
import { format, parseISO, isSameDay } from 'date-fns'

import { GameCard } from '../view/components/tournamentDetailsPanel/components/tournamentGamesTab/components/gameCard'
import * as styles from './styles'

const EventDetailsComponent = ({ eventId, tournamentGames, gamesLoading, gamesError }) => {
	const [currentDate, setCurrentDate] = useState(() => {
		// Default to current month/year
		const date = new Date()
		return date
	})
	const [selectedDate, setSelectedDate] = useState(() => {
		// Default to today
		const date = new Date()
		return date
	})
	const history = useHistory()

	// Update current date and selected date to first game's month when tournament games load
	useEffect(() => {
		if (tournamentGames && tournamentGames.length > 0) {
			// Find the first game with a valid date
			const firstGameWithDate = tournamentGames.find(game => game.startTime)
			if (firstGameWithDate) {
				try {
					const firstGameDate = parseISO(firstGameWithDate.startTime)

					// Set current date to the month of the first game
					const newCurrentDate = new Date(firstGameDate.getFullYear(), firstGameDate.getMonth(), 1)
					setCurrentDate(newCurrentDate)

					// Set selected date to the first game's date
					setSelectedDate(firstGameDate)
				} catch (error) {
					console.error('Error parsing first game date:', error)
				}
			}
		}
	}, [tournamentGames])

	const months = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'
	]

	const daysOfWeek = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

	const getDaysInMonth = (date) => {
		const year = date.getFullYear()
		const month = date.getMonth()
		const firstDay = new Date(year, month, 1)
		const lastDay = new Date(year, month + 1, 0)
		const daysCount = lastDay.getDate()

		// Get the day of week (0 = Sunday, 1 = Monday, etc.) and adjust for Monday start
		let startDay = firstDay.getDay()
		startDay = startDay === 0 ? 6 : startDay - 1 // Convert to Monday = 0

		const days = []

		// Add empty cells for days before the first day of the month
		for (let i = 0; i < startDay; i++) {
			days.push({ key: `empty-${year}-${month}-${i}`, day: null })
		}

		// Add all days of the month
		for (let day = 1; day <= daysCount; day++) {
			days.push({ key: `day-${year}-${month}-${day}`, day })
		}

		return days
	}

	const navigateMonth = (direction) => {
		const newDate = new Date(currentDate)
		newDate.setMonth(currentDate.getMonth() + direction)
		setCurrentDate(newDate)
	}

	const selectDate = (day) => {
		if (day) {
			const newSelectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
			setSelectedDate(newSelectedDate)
		}
	}

	const isSelected = (day) => {
		return day &&
			currentDate.getFullYear() === selectedDate.getFullYear() &&
			currentDate.getMonth() === selectedDate.getMonth() &&
			day === selectedDate.getDate()
	}

	const hasEvents = (day) => {
		if (!tournamentGames || tournamentGames.length === 0) {
			return false
		}

		// Check if any games exist on this day in the current month/year
		const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)

		const hasGames = tournamentGames.some(game => {
			if (!game.startTime) return false
			try {
				const gameDate = parseISO(game.startTime)
				const isSame = isSameDay(gameDate, targetDate)
				return isSame
			} catch (error) {
				return false
			}
		})

		return hasGames
	}

	const getGamesForSelectedDate = () => {
		if (!tournamentGames || tournamentGames.length === 0) {
			return []
		}

		// Filter games for the selected date - return raw API data
		const filteredGames = tournamentGames.filter(game => {
			if (!game.startTime) {
				return false
			}
			try {
				const gameDate = parseISO(game.startTime)
				const isSame = isSameDay(gameDate, selectedDate)
				return isSame
			} catch (error) {
				return false
			}
		})

		return filteredGames
	}

	const handleBack = () => {
		history.push(`/main/events/${eventId}`)
	}

	const days = getDaysInMonth(currentDate)
	let gamesContent

	if (gamesLoading) {
		gamesContent = (
			<Text size={16} css={styles.noEventsText}>
				Loading games...
			</Text>
		)
	} else if (gamesError) {
		gamesContent = (
			<Text size={16} css={styles.noEventsText}>
				Error loading games
			</Text>
		)
	} else {
		const games = getGamesForSelectedDate()

		if (games.length === 0) {
			gamesContent = (
				<Text size={14} css={styles.noEventsText}>
					No Events
				</Text>
			)
		} else {
			gamesContent = (
				<div css={styles.gamesContainer}>
					{games.map((game) => (
						<GameCard
							key={game.id}
							game={game}
						/>
					))}
				</div>
			)
		}
	}

	return (
		<div css={styles.container}>
			{/* Header */}
			<div css={styles.header}>
				<button
					type="button"
					css={styles.backArrow}
					onClick={handleBack}
					aria-label="Back to events"
				>
					<FontAwesomeIcon icon="arrow-left" />
				</button>
				<Text size={16} bold css={styles.headerTitle}>
					Details
				</Text>
			</div>

			{/* Calendar */}
			<div css={styles.calendarContainer}>
				<div css={styles.calendarHeader}>
					<Text size={18} bold css={styles.monthYear}>
						{months[currentDate.getMonth()]} {currentDate.getFullYear()}
					</Text>
					<div css={styles.navigation}>
						<button
							type="button"
							css={styles.navArrow}
							onClick={() => navigateMonth(-1)}
							aria-label="Previous month"
						>
							<FontAwesomeIcon icon="chevron-left" />
						</button>
						<button
							type="button"
							css={styles.navArrow}
							onClick={() => navigateMonth(1)}
							aria-label="Next month"
						>
							<FontAwesomeIcon icon="chevron-right" />
						</button>
					</div>
				</div>

				{/* Days of week */}
				<div css={styles.daysOfWeek}>
					{daysOfWeek.map((day) => (
						<div key={day} css={styles.dayOfWeek}>
							<Text size={12} css={styles.dayOfWeekText}>
								{day}
							</Text>
						</div>
					))}
				</div>

				{/* Calendar grid */}
				<div css={styles.calendarGrid}>
					{days.map(({ key, day }) => (
						<button
							type="button"
							key={key}
							css={[
								styles.dayCell,
								day && styles.dayActive,
								isSelected(day) && styles.daySelected,
							]}
							onClick={() => selectDate(day)}
							disabled={!day}
							aria-label={day ? `Select ${months[currentDate.getMonth()]} ${day}` : 'Empty calendar day'}
							aria-pressed={Boolean(day && isSelected(day))}
						>
							{day && (
								<>
									<Text size={14} css={styles.dayText}>
										{day}
									</Text>
									{hasEvents(day) && <div css={styles.eventDot} />}
								</>
							)}
						</button>
					))}
				</div>
			</div>

			{/* Games section */}
			<div css={styles.gamesSection}>
				<div css={styles.gamesTitleContainer}>
					<Text size={16} bold css={styles.gamesTitle}>
						Games
					</Text>
					<Text size={12} css={styles.gamesDate}>
						{format(selectedDate, 'MMM dd, yyyy')} (CEST, UTC+2)
					</Text>
				</div>

				{gamesContent}
			</div>
		</div>
	)
}

EventDetailsComponent.propTypes = {
	eventId: PropTypes.string.isRequired,
	event: PropTypes.shape({
		tournament: PropTypes.shape({
			id: PropTypes.number,
			tournamentName: PropTypes.string,
		}),
	}),
	tournamentGames: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		startTime: PropTypes.string,
		teamA: PropTypes.string,
		teamB: PropTypes.string,
		teamOne: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
		teamTwo: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
		resolved: PropTypes.bool,
		teamAScore: PropTypes.number,
		teamBScore: PropTypes.number,
	})),
	gamesLoading: PropTypes.bool,
	gamesError: PropTypes.string,
}

export default EventDetailsComponent 
